import type { ImagePoint, ManualQuad } from './types';

/**
 * Map the placed points (BL, TL, TR, BR — the tap order) to the quad
 * order `warpFromCorners` expects (TL, TR, BR, BL — matching the
 * auto-detector's output convention), scaling display → natural coords.
 * Exported for tests: getting this order wrong warps garbage, so the
 * contract is pinned by a unit test rather than trusted.
 */
export function quadFromPlacedCorners(
  placed: readonly ImagePoint[],
  invScale: number,
): ManualQuad | null {
  if (placed.length !== 4) return null;
  const [bl, tl, tr, br] = placed as [ImagePoint, ImagePoint, ImagePoint, ImagePoint];
  return [
    { x: tl.x * invScale, y: tl.y * invScale },
    { x: tr.x * invScale, y: tr.y * invScale },
    { x: br.x * invScale, y: br.y * invScale },
    { x: bl.x * invScale, y: bl.y * invScale },
  ];
}

/**
 * Inverse of quadFromPlacedCorners: a quad in natural coords (TL, TR,
 * BR, BL — the detector's / worker's convention) becomes tap-order
 * placed points (BL, TL, TR, BR) in display coords. Used to pre-seed
 * the crop modal with the auto-detected quad so re-cropping starts from
 * "drag the bad corner" instead of four taps from scratch.
 */
export function placedFromQuad(
  quad: ManualQuad,
  scale: number,
): [ImagePoint, ImagePoint, ImagePoint, ImagePoint] {
  const [tl, tr, br, bl] = quad;
  return [
    { x: bl.x * scale, y: bl.y * scale },
    { x: tl.x * scale, y: tl.y * scale },
    { x: tr.x * scale, y: tr.y * scale },
    { x: br.x * scale, y: br.y * scale },
  ];
}
